import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  RotateCcw, 
  Plus, 
  Eye, 
  Edit, 
  Check, 
  Clock, 
  Package,
  Building2,
  Search,
  Filter,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { retourService } from '../../services/retourService';
import { chantierService } from '../../services/chantierService';
import { RetourChantier } from '../../types';

function RetoursList() {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [retours, setRetours] = useState<RetourChantier[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    chantierId: ''
  });

  useEffect(() => {
    loadRetours();
  }, [filters]);

  const loadRetours = async () => {
    try {
      setLoading(true);
      const data = await retourService.getRetours(filters);
      setRetours(data);
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur de chargement',
        message: 'Impossible de charger les retours de chantier',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleValidateRetour = async (retourId: string) => {
    if (!user) return;
    
    try {
      await retourService.validateRetour(retourId, user.id);
      addNotification({
        userId: user.id,
        title: 'Retour validé',
        message: 'Le retour a été validé et le stock mis à jour',
        type: 'success'
      });
      loadRetours();
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur',
        message: 'Impossible de valider le retour',
        type: 'error'
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'en_attente':
        return <Clock className="h-4 w-4 text-orange-500" />;
      case 'valide':
        return <Check className="h-4 w-4 text-green-500" />;
      case 'refuse':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Package className="h-4 w-4 text-gray-500" />;
    }
  };

  const canManageRetours = user?.role === 'admin' || user?.role === 'responsable_depot' || user?.role === 'responsable_bureau';
  const canCreateRetours = canManageRetours || user?.role === 'responsable_equipe';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-700 rounded-lg shadow-lg">
                <RotateCcw className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Retours de chantier</h1>
            </div>
            
            {canCreateRetours && (
              <Link
                to="/retours/new"
                className="btn-primary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Nouveau retour</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card rounded-xl">
        <div className="px-6 py-5 sm:px-8">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white/80"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white/80"
            >
              <option value="">Tous les statuts</option>
              <option value="en_attente">En attente</option>
              <option value="valide">Validé</option>
              <option value="refuse">Refusé</option>
            </select>

            {/* Chantier Filter */}
            <select
              value={filters.chantierId}
              onChange={(e) => setFilters({ ...filters, chantierId: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white/80"
            >
              <option value="">Tous les chantiers</option>
              <option value="chantier-1">CH-2024-001 - Maison Dupont</option>
              <option value="chantier-2">CH-2024-002 - Extension Martin</option>
            </select>

            <button
              onClick={() => setFilters({ search: '', status: '', chantierId: '' })}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white/80 hover:bg-white transition-colors"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* Retours List */}
      <div className="glass-card rounded-xl">
        <div className="px-6 py-5 sm:px-8 border-b border-gray-200/50">
          <h3 className="text-lg font-medium text-gray-900">
            Retours de chantier ({retours.length})
          </h3>
        </div>

        {retours.length === 0 ? (
          <div className="text-center py-12">
            <RotateCcw className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun retour</h3>
            <p className="mt-1 text-sm text-gray-500">
              Les retours de chantier apparaîtront ici
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Retour
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Chantier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valeur retour
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white/50 divide-y divide-gray-200">
                {retours.map((retour) => (
                  <tr key={retour.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`p-2 rounded-lg mr-3 ${
                          retourService.getStatusColor(retour.status).replace('text-', 'text-').replace('bg-', 'bg-')
                        }`}>
                          {getStatusIcon(retour.status)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            #{retour.retourNumber}
                          </div>
                          <div className="text-sm text-gray-500">
                            {retour.items.length} article{retour.items.length > 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {chantierService.getChantierName(retour.chantierId)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        retourService.getStatusColor(retour.status)
                      }`}>
                        {retourService.getStatusLabel(retour.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {retour.totalValue.toFixed(2)} €
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(retour.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/retours/${retour.id}`}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Voir les détails"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        
                        {canManageRetours && retour.status === 'en_attente' && (
                          <>
                            <button
                              onClick={() => handleValidateRetour(retour.id)}
                              className="text-green-600 hover:text-green-900 p-1 rounded"
                              title="Valider le retour"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            
                            <Link
                              to={`/retours/${retour.id}/edit`}
                              className="text-gray-600 hover:text-gray-900 p-1 rounded"
                              title="Modifier"
                            >
                              <Edit className="h-4 w-4" />
                            </Link>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default RetoursList;